import React, { useState, useEffect } from "react";
import axios from "axios";
import { useData } from "../../components/toast/Api";
import MetaHead from "../../components/metaHead/MetaHead";
import { SEOData } from "../../types/home";
import ContactPage from "./ContactPage";

const apiUrl = import.meta.env.VITE_API_URL;

const Contact = () => {
  const { showSuccess, showError } = useData();

  const url = `${apiUrl}/api/openRoute/contactus`;

  const [formData, setFormData] = useState({
    name: "",
    number: "",
    email: "",
    message: "",
  });

  const [errors, setErrors] = useState<{
    name?: string;
    number?: string;
    email?: string;
    message?: string;
  }>({});

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [metaData, setMetaData] = useState<SEOData | null>(null);

  useEffect(() => {
    const data = {
      hidden: {
        heading: "",
        description: "",
      },
      seo: {
        shortcut_icon: {
          type: "image/x-icon",
          href: "https://goodhand.b-cdn.net/Assets/Good-Hand-02.png",
        },
        apple_touch_icon: {
          href: "https://goodhand.b-cdn.net/Assets/Good-Hand-02.png",
        },
        title: "Goodhand Contact Us",
        Keywords: "",
        description: "Goodhand Contact Us",
        canonical: "https://www.goodhand.ae/contactus",
        meta: [
          {
            name: "description",
            content: "Goodhand Contact Us",
          },
          {
            name: "url",
            content: "https://www.goodhand.ae/contactus",
          },
          {
            name: "og:title",
            content: "Goodhand Contact Us",
          },
          {
            name: "og:type",
            content: "website",
          },
          {
            name: "og:url",
            content: "https://www.goodhand.ae/contactus",
          },
          {
            name: "og:image",
            content:
              "https://goodhand.ideaintl.net/assets/logo-main-gFDCiCbr.svg",
          },
          {
            name: "og:site_name",
            content: "https://www.goodhand.ae",
          },
          {
            name: "og:description",
            content: "Goodhand Contact Us",
          },
          {
            name: "twitter:card",
            content: "summary_large_image",
          },
          {
            name: "twitter:title",
            content: "Goodhand Contact Us",
          },
          {
            name: "twitter:description",
            content: "Goodhand Contact Us",
          },
          {
            name: "twitter:image",
            content: "https://goodhand.b-cdn.net/Assets/Good-Hand-02.png",
          },
          {
            name: "twitter:site",
            content: "@GoodHandUAE",
          },
        ],
        scripts: [],
        noscript: [],
      },
    };
    setMetaData(data?.seo);
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value,
    });
  };

  const validate = () => {
    const newErrors: {
      name?: string;
      number?: string;
      email?: string;
      message?: string;
    } = {};
    const emailRegex = /^\S+@\S+\.\S+$/; // Regular expression for valid email
    const numberRegex = /^\d{1,11}$/; // Regular expression to validate up to 11 digits
    const nameRegex = /^[A-Za-z\s]+$/; // Regular expression to validate only English alphabets and spaces
    const messageRegex = /^[^0-9]*$/; // Ensures message is not just digits
    if (!formData.name) {
      newErrors.name = "Name is required";
    } else if (!nameRegex.test(formData.name)) {
      newErrors.name = "Name can only contain English alphabets";
    }
    if (!formData.number) {
      newErrors.number = "Number is required";
    } else if (!numberRegex.test(formData.number)) {
      newErrors.number = "Number must be a valid 11-digit number";
    }
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.message) {
      newErrors.message = "Message is required";
    } else if (!messageRegex.test(formData.message)) {
      newErrors.message = "Message cannot contain only digits";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const clearAllField = () => {
    setFormData({
      name: "",
      number: "",
      email: "",
      message: "",
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    if (validate()) {
      try {
        const response = await axios.post(url, formData);
        if (response) {
          clearAllField();
          showSuccess("Message sent successfully!");
        }
      } catch (error) {
        showError(
          "An error occurred while sending the message. Please try again."
        );
      } finally {
        setLoading(false);
      }
    } else {
      showError("Please Check all fields");
    }
  };

  return (
    <>
      {metaData && <MetaHead metaData={metaData} />}

      <div className="container-fluid p-0">
        {" "}
        <ContactPage />
      </div>
      <div className="contact-section container bg-2 mb-4">
        <div className="row justify-content-center">
          <div className="mt-5">
            <h2 className="font-25 ff-semibold text-center mb-2">
              Get in Touch with Goodhand Government LLC
            </h2>
            <p className="font-14 ff-regular text-center nw-help-desc mb-5">
              Whether you need Amer, Tasheel, Tawjeeh, or visa services, our
              team at Good Hand Government Transactions LLC is here to assist
              you every step of the way. Contact us today for expert guidance
              and hassle-free service!
            </p>
          </div>
          <div className="col-md-8 contact-form-section bg-2 py-2 px-4 rounded">
            <form className="form-main py-3" onSubmit={handleSubmit}>
              <div className="form-group py-3">
                <input
                  type="text"
                  className="form-control border-0"
                  id="name"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={handleChange}
                />
                {errors.name && (
                  <div className="alert alert-danger mt-3">{errors.name}</div>
                )}
              </div>
              <div className="form-group py-3">
                <input
                  type="number"
                  className="form-control border-0"
                  id="number"
                  placeholder="Enter your number"
                  value={formData.number}
                  onChange={handleChange}
                />
                {errors.number && (
                  <div className="alert alert-danger mt-3">{errors.number}</div>
                )}
              </div>
              <div className="form-group py-3">
                <input
                  type="email"
                  className="form-control border-0 "
                  id="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                />
                {errors.email && (
                  <div className="alert alert-danger mt-3">{errors.email}</div>
                )}
              </div>
              <div className="form-group py-3">
                <textarea
                  className="form-control border-0"
                  id="message"
                  placeholder="Your message"
                  value={formData.message}
                  onChange={handleChange}
                ></textarea>
                {errors.message && (
                  <div className="alert alert-danger mt-3">
                    {errors.message}
                  </div>
                )}
              </div>
              <div className="d-flex align-items-center justify-content-center">
                <button
                  type="submit"
                  className="btn bg-main text-white"
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Submit"}
                </button>
              </div>
            </form>
            {error && <div className="alert alert-danger mt-3">{error}</div>}
            {success && (
              <div className="alert alert-success mt-3">{success}</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
